import { RefObject } from "react";
import {
  UploadItem,
  UploadSource,
} from "@/components/upload/types/upload-item";
import { FolderMetadataMap } from "@/types/folder-metadata";
import { Item } from "../utils/workspaceItemUtils";
import { WorkspaceDocumentMetadata } from "@/models/metadata/metadata";
import { DownloadedFileWithIds } from "../providers/SharePointProvider";
import Workspace from "@/models/workspace/Workspace";

/**
 * Context props for the UploadHub provider
 */
export type UploadHubContextProps = {
  uploads: UploadItem[];
  fileInputRef: RefObject<HTMLInputElement>;
  folderInputRef: RefObject<HTMLInputElement>;
  setUploads: React.Dispatch<React.SetStateAction<UploadItem[]>>;
  onFileInputChange: (
    files: FileList,
    currentItems: Item[],
    folderId?: string
  ) => void;
  onFolderInputChange: (
    files: FileList,
    workspace: Workspace,
    folderId?: string
  ) => void;
  onUploadFilesFromComputer: () => void;
  onUploadFolderFromComputer: () => void;
  onMetadataCancel: () => void;
  onMetadataConfirm: (
    cleanedMetadata: WorkspaceDocumentMetadata[][]
  ) => Promise<void>;
  onOverwriteFilesCancel: () => void;
  onOverwriteFilesConfirm: () => void;
  handleFolderUpload: (
    files: FileList,
    workspace: Workspace,
    folderId?: string,
    providedMetadata?: Record<string, WorkspaceDocumentMetadata[]>,
    skipConflictCheck?: boolean,
    skipMetadataCheck?: boolean,
    fileMetadataMap?: Map<
      string,
      {
        source: UploadSource;
        driveId?: string;
        siteId?: string;
        sharepointFileId?: string;
      }
    >,
    folderMetadataMap?: FolderMetadataMap
  ) => Promise<void>;
  createFileListFromDownloadedFiles: (
    filesWithIds: DownloadedFileWithIds[]
  ) => {
    fileList: FileList;
    metadataMap: Map<string, DownloadedFileWithIds>;
  };
};

/**
 * SignalR hub events for upload notifications
 */
export enum UploadHubEvent {
  SendIndexingStatusMessage = "SendIndexingStatusMessage",
  WorkspaceUpdated = "WorkspaceUpdated",
}

/**
 * Payload for indexing status messages from the hub
 */
export type IndexingStatusPayload = {
  documentId: string;
  status:
    | "Processing"
    | "Indexing"
    | "Indexed"
    | "Failed"
    | "IndexingFailed"
    | "Completed";
  documentName: string;
  documentBlobName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  feedback?: any;
  /** With "Indexing": the worker's operation id, pollable via index-status. */
  operationId?: string;
  /** With "Completed": indexing was handed to a background run (LAR-412). */
  indexingInBackground?: boolean;
};

/**
 * One entry of the reconnect snapshot (GET workspace/{id}/uploads/status),
 * for uploads whose SignalR events this client may have missed.
 */
export type UploadStatusSnapshotEntry = {
  /**
   * "Completed" | "Indexing" | "IndexingFailed" | "Failed" | "Unknown".
   * Typed as string because it arrives as one; the mapper ignores anything else.
   */
  status: string;
  documentId?: string | null;
  documentName?: string | null;
  isRevision?: boolean;
};

/**
 * File metadata for SharePoint/OneDrive files
 */
export type SharePointFileMetadata = {
  source: UploadSource;
  driveId?: string;
  siteId?: string;
  sharepointFileId?: string;
};

/**
 * File with path information for folder uploads
 */
export type FileWithRelativePath = File & {
  webkitRelativePath?: string;
  _sharePointMetadata?: SharePointFileMetadata;
};

/**
 * Document creation request for batch upload
 */
export type DocumentUploadRequest = {
  documentName: string;
  documentBlobName: string;
  documentSource: UploadSource;
  driveId?: string;
  siteId?: string;
  sharepointFileId?: string;
  documentId?: string;
  documentMetadataList: WorkspaceDocumentMetadata[];
  folderId?: string;
};

/**
 * Result of folder conflict check
 */
export type FolderConflictResult = {
  existingPaths: Set<string>;
  newPaths: string[];
};
